import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import AddMovieHeader from "../components/AddMovieHeader";
import PageHeader from "../components/Header";
import Input from "components/Input";
import CategoryModal from "components/CategoryModal";
import YearModal from "components/YearModal";
import CountryModal from "components/CountryModal";
import LanguageModal from "components/LanguageModal";
import ClassificationModal from "components/ClassificationModal";
import { toast } from "react-toastify";
import useMutation from "hooks/useMutation";
import useSWR, { useSWRConfig } from "swr";
import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import MovieAudioType from "components/MovieAudioType";

export default function MovieDetails({ onCancel, details }) {
  const navigate = useNavigate();
  const moviebox = useSelector((state) => state?.moviebox?.formData);

  // Validation schema
  const validationSchema = yup.object().shape({
    title: yup.string().required("Title is required"),
    description: yup.string().required("Description is required"),
    categories: yup.array().min(1, "At least one category is required"),
    year: yup.string().required("Release year is required"),
    country: yup.string().required("Country is required"),
    language: yup.string().required("Language is required"),
    classifications: yup
      .array()
      .min(1, "At least one classification is required"),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = methods;

  console.log(watch())
  const [searchParams] = useSearchParams();
  const { data: media_types } = useSWR("/entertainment/movie-box/media-types");
  const media_type_id = media_types?.data?.find(
    (mt) =>
      mt.display_name?.toLowerCase() ===
      (details?.media_type?.toLowerCase() || searchParams.get("media_type"))
  )?.id;
  const { loading, mutate } = useMutation({
    method: details?.id ? "put" : "post",
    url:
      "/entertainment/movie-box/medias/basic-information" +
      (details?.id ? `/${details.id}` : ""),
  });
  const { mutate: updateMovies } = useSWRConfig();

  useEffect(() => {
    if (!details && !moviebox) return;

    console.log(moviebox);
    setValue("title", details?.title || moviebox?.title || "");
    setValue(
      "description",
      details?.description || moviebox?.description || ""
    );
    setValue(
      "categories",
      details?.genres?.map((genre) => genre?.display_name) ||
        moviebox?.category ||
        []
    );
    setValue("year", details?.release_year || moviebox?.releaseYear || "");
    setValue(
      "country",
      details?.country?.id || parseInt(moviebox?.country) || ""
    );
    setValue(
      "language",
      details?.language?.id || parseInt(moviebox?.language) || ""
    );
    setValue(
      "classifications",
      details?.classifications?.map((c) => ({
        display_name: c?.display_name,
        id: c?.id,
        name: c?.name,
      })) ||
        moviebox?.classifications ||
        []
    );
  }, [details, moviebox, setValue]);

  const handleCancel = () => {
    if (typeof onCancel === "function") onCancel();
    navigate(-1);
  };
  const onSubmit = (data) => {
    const payload = {
      media_info: {
        id: details?.id || undefined,
        title: data?.title,
        alternative_title: "",
        tag_line: "",
        description: data?.description,
        release_year: data?.year,
        language_id: data?.language ?? null,
        country_id: data?.country ?? null,
        agreed_to_terms_and_conditions: true,
        audio_version: data?.audio_version,
        media_type_id,
      },
      genres: data?.categories,
      classifications: data?.classifications?.map((c) => c?.display_name),
    };

    mutate({
      payload,
      onSuccess(res) {
        toast.success(
          `Movie ${details?.id ? "updated" : "added"} successfully`
        );
        updateMovies("/entertainment/movie-box/medias");
        navigate(
          "/movie-box/add-movie/cast-crew" +
            (details?.id ? `?id=${details.id}` : `?id=${res?.data?.id}`)
        );
      },
      onError(err) {
        console.log(err);
        JSON.parse(err?.message)?.message?.map(toast.error);
      },
    });
  };

  // Reusable Field Wrapper
  const FieldWrapper = useCallback(
    ({ label, required, children }) => (
      <div className="flex flex-col">
        {label && (
          <label className="text-sm font-medium text-gray-700 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        )}
        {children}
      </div>
    ),
    []
  );

  return (
    <FormProvider {...methods}>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <AddMovieHeader
          onCancel={handleCancel}
          showProgress={true}
          currentStep={1}
          onContinue={handleSubmit(onSubmit)}
          loading={loading}
        />
        <main className="flex-1 flex justify-center px-4 py-8">
          <div className="w-full max-w-lg bg-white p-6 rounded-md shadow-sm">
            <PageHeader
              title={"General Information"}
              description={
                "Share the essential info about your movie to help users find it easily"
              }
            />
            <div className="space-y-6">
              <Input
                name="title"
                placeholder="Enter movie title"
                required
                error={errors?.title?.message}
                label="Movie Title"
              />
              <Input
                name="description"
                label="Movie Description"
                placeholder="Describe the movie..."
                required
                type="textarea"
                rows={3}
                error={errors?.description?.message}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FieldWrapper label="Release Year" required>
                  <YearModal name={"year"} error={errors?.year?.message} />
                </FieldWrapper>
                <FieldWrapper required>
                  <CountryModal
                    name="country"
                    error={errors?.country?.message}
                  />
                </FieldWrapper>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* <FieldWrapper required>  */}
                
                       <MovieAudioType /> 
                       {/* </FieldWrapper> */}
                <FieldWrapper required>
                  <LanguageModal
                    name="language"
                    error={errors?.language?.message}
                  />
                </FieldWrapper>
              </div>

  <CategoryModal
                    name="categories"
                    error={errors?.categories?.message}
                    label={"Category"}
                  />
        
                <FieldWrapper required>
                  <ClassificationModal
                    name="classifications"
                    error={errors?.classifications?.message}
                  />
                </FieldWrapper>

            </div>
          </div>
        </main>
      </div>
    </FormProvider>
  );
}
